// Widget renderers for the ask canvas v2 (spec 16).
//
// One renderer per kind. The page-level `<Canvas />` is responsible
// for canonical ordering + two-column desktop layout. Widgets here
// are self-contained presentational components.

import { useRef, useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, Hash } from "lucide-react";

import { GlassPanel, Pill } from "@/components/glass";
import { Prose } from "@/components/canvas/prose";
import type {
	AnswerProseProps,
	ClosestMatchesProps,
	DiagnosticProps,
	EntityCardProps,
	FollowupsProps,
	KeyFactsProps,
	SourceItem,
	SourcesStripProps,
} from "@/components/canvas/types";

// ---------------------------------------------------------------------------
// SourcesStrip — horizontal pill row anchored ABOVE the prose answer.
//
// Cards are small; the title is truncated to one line, type label is
// uppercase tracking-wider. Numbered ref is the visual anchor that
// matches the inline citation chips in the prose.

interface SourcesStripCompProps extends SourcesStripProps {
	highlightedRef?: number | null;
	onHover?: (ref: number | null) => void;
	onFocusNode?: (nodeId: string) => void;
}

export function SourcesStrip({
	sources,
	highlightedRef,
	onHover,
	onFocusNode,
}: SourcesStripCompProps) {
	if (sources.length === 0) return null;
	// CSS spec: setting overflow-x to anything but visible forces
	// overflow-y to auto too, so the scroll container clips vertically.
	// Give the strip generous vertical padding so the cards' borders
	// (and the focus ring, which extends 1px outside the border) sit
	// well inside the clip boundary.
	return (
		<div className="space-y-2">
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
				Sources
			</div>
			<div className="flex gap-2 overflow-x-auto py-2 -my-2 -mx-2 px-2">
				{sources.map((s) => (
					<SourceCard
						key={s.ref}
						source={s}
						highlighted={highlightedRef === s.ref}
						onHover={onHover}
						onFocusNode={onFocusNode}
					/>
				))}
			</div>
		</div>
	);
}

interface SourceCardProps {
	source: SourceItem;
	highlighted: boolean;
	onHover?: (ref: number | null) => void;
	onFocusNode?: (nodeId: string) => void;
}

function SourceCard({
	source,
	highlighted,
	onHover,
	onFocusNode,
}: SourceCardProps) {
	const clickable = !!onFocusNode;
	return (
		<button
			type={clickable ? "button" : undefined}
			id={`source-${source.ref}`}
			onMouseEnter={() => onHover?.(source.ref)}
			onMouseLeave={() => onHover?.(null)}
			onClick={() => onFocusNode?.(source.id)}
			disabled={!clickable}
			title={clickable ? "Open in graph" : undefined}
			className={`shrink-0 w-[220px] flex items-start gap-2 px-3 py-2 rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] transition-colors text-left ${
				clickable ? "cursor-pointer hover:bg-[var(--color-surface-hi)] hover:border-[var(--color-border-hi)]" : ""
			} ${
				highlighted ? "ring-1 ring-white/20 bg-[var(--color-surface-hi)]" : ""
			}`}
		>
			<span className="shrink-0 inline-flex items-center justify-center w-[18px] h-[18px] mt-px rounded-[4px] text-[10px] mono bg-white/[0.08] text-[var(--color-text-muted)]">
				{source.ref}
			</span>
			<div className="flex-1 min-w-0">
				<div className="text-[12px] text-[var(--color-text)] truncate">
					{source.title}
				</div>
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mt-0.5">
					{source.type}
				</div>
				{source.snippet && (
					<div
						className="text-[11px] text-[var(--color-text-muted)] mt-1 line-clamp-2"
						style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
					>
						{source.snippet}
					</div>
				)}
			</div>
		</button>
	);
}

// ---------------------------------------------------------------------------
// AnswerProse — markdown rendered, primary content.
//
// Wraps the Prose component (which handles markdown + citation chips).
// No GlassPanel container — the prose IS the content; chrome would
// compete with it. The canvas spaces it with margin only.

interface AnswerProseCompProps extends AnswerProseProps {
	knownRefs?: Set<number>;
	onCiteClick?: (ref: number) => void;
}

export function AnswerProse({
	text,
	knownRefs,
	onCiteClick,
}: AnswerProseCompProps) {
	return <Prose text={text} knownRefs={knownRefs} onCiteClick={onCiteClick} />;
}

// ---------------------------------------------------------------------------
// EntityCard — featured KB node. Lives in the right rail on desktop.
// Clickable when `onFocusNode` is supplied → routes to the graph view
// centered on this node.

interface EntityCardCompProps extends EntityCardProps {
	onFocusNode?: (nodeId: string) => void;
}

export function EntityCard({
	node_id,
	name,
	type,
	description,
	attributes,
	onFocusNode,
}: EntityCardCompProps) {
	const clickable = !!onFocusNode;
	const body = (
		<>
			<div className="flex items-baseline gap-2 mb-1">
				<Hash className="w-3 h-3 text-[var(--color-text-dim)] shrink-0" />
				<span className="text-[12.5px] font-medium truncate">{name}</span>
				<span className="text-[9.5px] uppercase tracking-wider text-[var(--color-text-dim)] ml-auto">
					{type}
				</span>
			</div>
			{description && (
				<div className="text-[11.5px] text-[var(--color-text-muted)] mb-2 leading-[1.45]">
					{description}
				</div>
			)}
			{attributes.length > 0 && (
				<div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
					{attributes.map((a, i) => (
						<div key={i} className="contents">
							<div className="text-[var(--color-text-dim)] mono">{a.key}</div>
							<div className="text-[var(--color-text-muted)] truncate">
								{a.value}
							</div>
						</div>
					))}
				</div>
			)}
		</>
	);
	if (!clickable) {
		return <GlassPanel className="px-3 py-2.5">{body}</GlassPanel>;
	}
	return (
		<button
			type="button"
			onClick={() => onFocusNode?.(node_id)}
			title="Open in graph"
			className="block w-full text-left glass rounded-[var(--radius-panel)] px-3 py-2.5 cursor-pointer transition-colors hover:bg-[var(--color-surface-hi)] hover:border-[var(--color-border-hi)]"
		>
			{body}
		</button>
	);
}

// ---------------------------------------------------------------------------
// KeyFacts — inline structured facts (small, no glass container).

export function KeyFacts({ title, rows }: KeyFactsProps) {
	if (rows.length === 0) return null;
	return (
		<div className="space-y-1.5">
			{title && (
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
					{title}
				</div>
			)}
			<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
				{rows.map((r, i) => (
					<div key={i} className="contents">
						<div className="text-[var(--color-text-dim)] mono text-[11px]">
							{r.key}
						</div>
						<div
							className={
								r.emphasis
									? "text-[var(--color-text)] font-medium"
									: "text-[var(--color-text-muted)]"
							}
						>
							{r.value}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Followups — pill row at the bottom of the canvas.

interface FollowupsCompProps extends FollowupsProps {
	onFollowup?: (query: string) => void;
}

export function Followups({ items, onFollowup }: FollowupsCompProps) {
	if (items.length === 0) return null;
	return (
		<div className="space-y-2">
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
				Related
			</div>
			<div className="flex flex-wrap gap-2">
				{items.map((p, i) => (
					<Pill key={i} onClick={() => onFollowup?.(p.query)}>
						{p.label}
					</Pill>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Diagnostic — collapsible retrieval stats.

export function Diagnostic({
	mode_selected,
	top_k_fetched,
	max_score,
	hit_breakdown,
}: DiagnosticProps) {
	const [open, setOpen] = useState(false);
	return (
		<div className="rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="w-full flex items-center gap-2 px-3 py-2 text-left text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors"
			>
				{open ? (
					<ChevronDown className="w-3 h-3" />
				) : (
					<ChevronRight className="w-3 h-3" />
				)}
				<span className="text-[10px] uppercase tracking-wider">
					Retrieval diagnostic
				</span>
				<span className="text-[10px] mono text-[var(--color-text-dim)] ml-auto">
					mode={mode_selected} top1={max_score.toFixed(3)}
				</span>
			</button>
			{open && (
				<div className="px-3 pb-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] mono">
					<div className="text-[var(--color-text-dim)]">mode_selected</div>
					<div className="text-[var(--color-text-muted)]">{mode_selected}</div>
					<div className="text-[var(--color-text-dim)]">top_k_fetched</div>
					<div className="text-[var(--color-text-muted)]">{top_k_fetched}</div>
					<div className="text-[var(--color-text-dim)]">max_score</div>
					<div className="text-[var(--color-text-muted)]">
						{max_score.toFixed(3)}
					</div>
					<div className="text-[var(--color-text-dim)]">chunks</div>
					<div className="text-[var(--color-text-muted)]">
						{hit_breakdown.chunk}
					</div>
					<div className="text-[var(--color-text-dim)]">entities</div>
					<div className="text-[var(--color-text-muted)]">
						{hit_breakdown.entity}
					</div>
					<div className="text-[var(--color-text-dim)]">communities</div>
					<div className="text-[var(--color-text-muted)]">
						{hit_breakdown.community}
					</div>
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// ClosestMatches — shown when retrieval was below sanity floor.

export function ClosestMatches({ items }: ClosestMatchesProps) {
	if (items.length === 0) return null;
	return (
		<GlassPanel className="px-4 py-3">
			<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-2 flex items-center gap-2">
				<BookOpen className="w-3 h-3 text-amber-300/70" />
				<span>Weak match · closest hits below sanity floor</span>
			</div>
			<div className="space-y-1">
				{items.map((m) => (
					<div key={m.id} className="flex items-center gap-3 text-[11px]">
						<span className="text-[var(--color-text-dim)] uppercase tracking-wider min-w-[64px]">
							{m.type}
						</span>
						<span className="flex-1 truncate text-[var(--color-text-muted)]">
							{m.title}
						</span>
						<span className="mono text-[var(--color-text-dim)]">
							{m.score.toFixed(3)}
						</span>
					</div>
				))}
			</div>
		</GlassPanel>
	);
}

// ---------------------------------------------------------------------------
// Stagger reveal — used by the page-level Canvas to fade widgets in
// in canonical order without animation libs.

interface StaggerProps {
	index: number;
	children: React.ReactNode;
}

export function Stagger({ index, children }: StaggerProps) {
	const ref = useRef<HTMLDivElement | null>(null);
	return (
		<div
			ref={ref}
			style={{
				opacity: 0,
				transform: "translateY(4px)",
				animation: `canvas-in 280ms ease-out forwards`,
				animationDelay: `${index * 70}ms`,
			}}
		>
			{children}
		</div>
	);
}
